'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { MapPin, Layers, Navigation, ZoomIn, ZoomOut, X, Bed, Maximize, TrendingUp } from 'lucide-react';

// Backend integration point: GET /api/properties/map?bounds=...&filters=...
// Map provider integration point: Mapbox or Google Maps token from MAP_PROVIDER env

interface MapMarker {
  id: string;
  lat: number;
  lng: number;
  price: string;
  priceNum: number;
  title: string;
  bedrooms: number;
  area: number;
  listingType: 'satilik' | 'kiralik';
  image: string;
  imageAlt: string;
  score: number;
}

const MAP_MARKERS: MapMarker[] = [
{ id: 'marker-001', lat: 40.9908, lng: 29.0291, price: '₺12.75M', priceNum: 12750000, title: 'Moda\'da Deniz Manzaralı 3+1', bedrooms: 3, area: 150, listingType: 'satilik', image: "https://img.rocket.new/generatedImages/rocket_gen_img_159aee91e-1783748260808.png", imageAlt: 'Moda daire salon ve mutfak', score: 82 },
{ id: 'marker-002', lat: 40.9731, lng: 29.0352, price: '₺8.9M', priceNum: 8900000, title: 'Fenerbahçe 3+1 Yeni Bina', bedrooms: 3, area: 135, listingType: 'satilik', image: "https://images.unsplash.com/photo-1640190624040-62d153b59455", imageAlt: 'Modern daire iç mekanı', score: 79 },
{ id: 'marker-003', lat: 40.9650, lng: 29.0580, price: '₺10.2M', priceNum: 10200000, title: 'Bağdat Cad. Rezidans 3+1', bedrooms: 3, area: 162, listingType: 'satilik', image: "https://images.unsplash.com/photo-1716494319097-1545c5a255ba", imageAlt: 'Rezidans daire balkon manzarası', score: 76 },
{ id: 'marker-004', lat: 40.9810, lng: 29.0480, price: '₺28K/ay', priceNum: 28000, title: 'Acıbadem 1+1 Kiralık', bedrooms: 1, area: 60, listingType: 'kiralik', image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e972e1aa-1772793487683.png", imageAlt: 'Küçük modern daire', score: 61 },
{ id: 'marker-005', lat: 40.9560, lng: 29.0640, price: '₺7.2M', priceNum: 7200000, title: 'Suadiye 2+1 Denize Yakın', bedrooms: 2, area: 90, listingType: 'satilik', image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f87baa61-1776096341644.png", imageAlt: 'Sahil daire manzarası', score: 84 },
{ id: 'marker-006', lat: 40.9720, lng: 29.1000, price: '₺18.9M', priceNum: 18900000, title: 'Caddebostan 4+1 Lüks', bedrooms: 4, area: 200, listingType: 'satilik', image: "https://img.rocket.new/generatedImages/rocket_gen_img_18c6d33c5-1769432645647.png", imageAlt: 'Lüks daire deniz manzarası', score: 71 }];


const HEATMAP_MODES = [
{ value: 'satilik-m2', label: 'Satılık m² Fiyatı' },
{ value: 'kiralik-m2', label: 'Kiralık m² Fiyatı' },
{ value: 'kira-getirisi', label: 'Kira Getirisi' },
{ value: 'fiyat-degisimi', label: 'Fiyat Değişimi' },
{ value: 'ilan-yogunlugu', label: 'İlan Yoğunluğu' }];


export default function MapPanel({ selectedPropertyId, onSelectProperty


}: {selectedPropertyId: string | null;onSelectProperty: (id: string | null) => void;}) {
  const [activeMarker, setActiveMarker] = useState<MapMarker | null>(null);
  const [showLayers, setShowLayers] = useState(false);
  const [activeHeatmap, setActiveHeatmap] = useState<string | null>(null);
  const [showSearchArea, setShowSearchArea] = useState(false);

  const handleMarkerClick = (marker: MapMarker) => {
    setActiveMarker(activeMarker?.id === marker.id ? null : marker);
    onSelectProperty(marker.id === selectedPropertyId ? null : marker.id);
  };

  return (
    <div className="relative w-full h-full bg-slate-200 overflow-hidden">
      {/* Map Placeholder — replace with Mapbox/Google Maps */}
      {/* Backend integration point: Initialize MapboxGL or Google Maps here */}
      <div className="absolute inset-0 map-placeholder">
        {/* Simulated map grid */}
        <svg className="absolute inset-0 w-full h-full opacity-20" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="mapGrid" width="60" height="60" patternUnits="userSpaceOnUse">
              <path d="M 60 0 L 0 0 0 60" fill="none" stroke="#6366f1" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#mapGrid)" />
        </svg>

        {/* Simulated roads */}
        <svg className="absolute inset-0 w-full h-full opacity-30" xmlns="http://www.w3.org/2000/svg">
          <line x1="0" y1="40%" x2="100%" y2="45%" stroke="white" strokeWidth="3" />
          <line x1="0" y1="65%" x2="100%" y2="60%" stroke="white" strokeWidth="2" />
          <line x1="30%" y1="0" x2="35%" y2="100%" stroke="white" strokeWidth="3" />
          <line x1="60%" y1="0" x2="65%" y2="100%" stroke="white" strokeWidth="2" />
          <line x1="0" y1="20%" x2="100%" y2="25%" stroke="white" strokeWidth="1.5" />
          <line x1="15%" y1="0" x2="20%" y2="100%" stroke="white" strokeWidth="1.5" />
          <line x1="80%" y1="0" x2="78%" y2="100%" stroke="white" strokeWidth="1.5" />
        </svg>

        {/* Heatmap overlay */}
        {activeHeatmap &&
        <div className="absolute inset-0 opacity-40"
        style={{
          background: 'radial-gradient(ellipse at 30% 40%, rgba(220,38,38,0.7) 0%, rgba(234,179,8,0.5) 30%, rgba(22,163,74,0.3) 60%, transparent 80%), radial-gradient(ellipse at 70% 60%, rgba(27,79,255,0.6) 0%, rgba(99,102,241,0.4) 40%, transparent 70%)'
        }} />

        }

        {/* Map Markers */}
        {MAP_MARKERS.map((marker, i) => {
          const leftPct = 15 + i * 14 % 70;
          const topPct = 20 + i * 11 % 55;
          const isActive = activeMarker?.id === marker.id || selectedPropertyId === marker.id;

          return (
            <button
              key={marker.id}
              onClick={() => handleMarkerClick(marker)}
              className={`absolute transform -translate-x-1/2 -translate-y-1/2 transition-all duration-200 z-10 ${isActive ? 'z-20 scale-110' : 'hover:scale-105 hover:z-20'}`}
              style={{ left: `${leftPct}%`, top: `${topPct}%` }}>
              
              <div className={`px-3 py-1.5 rounded-xl text-xs font-bold shadow-lg whitespace-nowrap transition-all ${isActive ? 'bg-primary text-white scale-110 shadow-xl' : marker.listingType === 'satilik' ? 'bg-white text-foreground hover:bg-primary hover:text-white' : 'bg-accent text-white'}`}>
                {marker.price}
              </div>
              <div className={`w-2 h-2 rounded-full mx-auto mt-0.5 ${isActive ? 'bg-primary' : marker.listingType === 'satilik' ? 'bg-foreground' : 'bg-accent'}`} />
            </button>);

        })}

        {/* Cluster marker */}
        <div className="absolute" style={{ left: '82%', top: '72%' }}>
          <div className="w-10 h-10 rounded-full bg-primary text-white text-sm font-bold flex items-center justify-center shadow-lg border-2 border-white">
            14
          </div>
        </div>

        {/* Area label */}
        <div className="absolute" style={{ left: '45%', top: '48%' }}>
          <span className="text-sm font-bold text-foreground/60 select-none">Kadıköy</span>
        </div>
        <div className="absolute" style={{ left: '68%', top: '35%' }}>
          <span className="text-xs font-semibold text-foreground/40 select-none">Ataşehir</span>
        </div>
        <div className="absolute" style={{ left: '22%', top: '62%' }}>
          <span className="text-xs font-semibold text-foreground/40 select-none">Maltepe</span>
        </div>
      </div>

      {/* "Bu Alanı Ara" button */}
      {showSearchArea &&
      <div className="absolute top-16 left-1/2 -translate-x-1/2 z-30 animate-fade-in">
          <button
          onClick={() => setShowSearchArea(false)}
          className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-foreground text-white text-sm font-semibold shadow-xl hover:bg-primary transition-colors">
          
            <MapPin size={16} />
            Bu Alanı Ara
          </button>
        </div>
      }

      {/* Map Controls */}
      <div className="absolute right-4 top-4 flex flex-col gap-2 z-20">
        {/* Layers */}
        <div className="relative">
          <button
            onClick={() => setShowLayers(!showLayers)}
            className={`w-10 h-10 rounded-xl shadow-md flex items-center justify-center transition-all ${showLayers ? 'bg-primary text-white' : 'bg-white text-foreground hover:bg-muted'}`}
            title="Katmanlar">
            
            <Layers size={18} />
          </button>

          {showLayers &&
          <div className="absolute right-12 top-0 w-52 bg-white rounded-xl shadow-xl border border-border overflow-hidden animate-fade-in">
              <div className="p-3 border-b border-border">
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Isı Haritası</p>
              </div>
              <div className="p-2">
                <button
                onClick={() => setActiveHeatmap(null)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeHeatmap === null ? 'bg-primary-light text-primary' : 'text-foreground hover:bg-muted'}`}>
                
                  Kapalı
                </button>
                {HEATMAP_MODES.map((mode) =>
              <button
                key={`hm-${mode.value}`}
                onClick={() => {setActiveHeatmap(mode.value);setShowLayers(false);}}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeHeatmap === mode.value ? 'bg-primary-light text-primary' : 'text-foreground hover:bg-muted'}`}>
                
                    {mode.label}
                  </button>
              )}
              </div>
            </div>
          }
        </div>

        <button
          onClick={() => setShowSearchArea(true)}
          className="w-10 h-10 rounded-xl bg-white shadow-md flex items-center justify-center text-foreground hover:bg-muted transition-all"
          title="Konumuma Git">
          
          <Navigation size={18} />
        </button>

        <button className="w-10 h-10 rounded-xl bg-white shadow-md flex items-center justify-center text-foreground hover:bg-muted transition-all" title="Yakınlaştır">
          <ZoomIn size={18} />
        </button>
        <button className="w-10 h-10 rounded-xl bg-white shadow-md flex items-center justify-center text-foreground hover:bg-muted transition-all" title="Uzaklaştır">
          <ZoomOut size={18} />
        </button>
      </div>

      {/* Active Heatmap Badge */}
      {activeHeatmap &&
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20">
          <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-foreground/90 text-white text-xs font-semibold shadow-lg">
            <Layers size={12} />
            {HEATMAP_MODES.find((m) => m.value === activeHeatmap)?.label}
            <button onClick={() => setActiveHeatmap(null)} className="ml-1 opacity-70 hover:opacity-100">
              <X size={12} />
            </button>
          </div>
        </div>
      }

      {/* Property Popup */}
      {activeMarker &&
      <div className="absolute bottom-4 left-4 right-4 md:left-4 md:right-auto md:w-72 z-30 animate-slide-up">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-border">
            <button
            onClick={() => {setActiveMarker(null);onSelectProperty(null);}}
            className="absolute top-2 right-2 z-10 w-6 h-6 rounded-full bg-white/80 flex items-center justify-center text-foreground hover:bg-white shadow-sm">
            
              <X size={12} />
            </button>
            <div className="relative h-36 overflow-hidden">
              <img
              src={activeMarker.image}
              alt={activeMarker.imageAlt}
              className="w-full h-full object-cover" />
            
              <span className={`absolute top-2 left-2 text-xs font-bold px-2 py-0.5 rounded-md ${activeMarker.listingType === 'satilik' ? 'bg-primary text-white' : 'bg-accent text-white'}`}>
                {activeMarker.listingType === 'satilik' ? 'Satılık' : 'Kiralık'}
              </span>
            </div>
            <div className="p-3">
              <p className="text-sm font-bold text-foreground line-clamp-1 mb-1">{activeMarker.title}</p>
              <p className="text-lg font-extrabold text-primary tabular-nums mb-2">{activeMarker.price}</p>
              <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                <span className="flex items-center gap-1"><Bed size={12} className="text-primary" />{activeMarker.bedrooms}+1</span>
                <span className="flex items-center gap-1"><Maximize size={12} className="text-primary" />{activeMarker.area}m²</span>
                <span className={`ml-auto flex items-center gap-1 font-bold text-xs px-2 py-0.5 rounded-md ${activeMarker.score >= 80 ? 'bg-success-light text-success' : 'bg-warning-light text-warning'}`}>
                  <TrendingUp size={10} />
                  {activeMarker.score}/100
                </span>
              </div>
              <Link href="/property-detail" className="btn-primary w-full justify-center text-xs py-2">
                İlanı İncele
              </Link>
            </div>
          </div>
        </div>
      }

      {/* Map Attribution */}
      <div className="absolute bottom-2 right-2 text-[10px] text-foreground/30 font-medium">
        © Evora Harita · Veriler tahminidir
      </div>
    </div>);

}